import { useSyncExternalStore } from "react";
import type { ApprovalRequest, Chat, ChatMessageRecord } from "../types";

export interface ChatsState {
  chats: Chat[];
  // Chats currently open as panes in the workbench grid. The order is the
  // grid order. activeId is the *focused* pane (drives the right-rail
  // inspector + which pane keyboard actions target).
  openChats: string[];
  activeId: string | null;
  unread: Set<string>;
  pendingApprovals: Map<string, ApprovalRequest[]>;
  error: string | null;
  loading: boolean;
}

const initial = (): ChatsState => ({
  chats: [],
  openChats: [],
  activeId: null,
  unread: new Set<string>(),
  pendingApprovals: new Map<string, ApprovalRequest[]>(),
  error: null,
  loading: false,
});

let state: ChatsState = initial();
const subs = new Set<() => void>();

function notify() {
  for (const s of subs) s();
}

function set(updater: (prev: ChatsState) => ChatsState) {
  state = updater(state);
  notify();
}

export const chatsStore = {
  getSnapshot: () => state,
  subscribe: (fn: () => void) => {
    subs.add(fn);
    return () => subs.delete(fn);
  },
  reset: () => {
    state = initial();
    notify();
  },
  setChats: (chats: Chat[]) => set((s) => ({ ...s, chats })),
  setActive: (id: string | null) =>
    set((s) => {
      const unread = new Set(s.unread);
      if (id) unread.delete(id);
      return { ...s, activeId: id, unread };
    }),
  // Open a chat as a pane in the grid (no-op if already open) and focus it.
  openChat: (id: string) =>
    set((s) => {
      const unread = new Set(s.unread);
      unread.delete(id);
      const openChats = s.openChats.includes(id) ? s.openChats : [...s.openChats, id];
      return { ...s, openChats, activeId: id, unread };
    }),
  // Close a chat pane. If it was focused, focus the last remaining open pane.
  closeChat: (id: string) =>
    set((s) => {
      const openChats = s.openChats.filter((c) => c !== id);
      const activeId = s.activeId === id ? (openChats[openChats.length - 1] ?? null) : s.activeId;
      return { ...s, openChats, activeId };
    }),
  upsert: (chat: Chat) =>
    set((s) => {
      const idx = s.chats.findIndex((c) => c.id === chat.id);
      const next = [...s.chats];
      if (idx >= 0) next[idx] = chat;
      else next.push(chat);
      return { ...s, chats: next };
    }),
  remove: (id: string) =>
    set((s) => {
      const openChats = s.openChats.filter((c) => c !== id);
      return {
        ...s,
        chats: s.chats.filter((c) => c.id !== id),
        openChats,
        activeId: s.activeId === id ? (openChats[openChats.length - 1] ?? null) : s.activeId,
      };
    }),
  appendMessage: (chatId: string, msg: ChatMessageRecord) =>
    set((s) => ({
      ...s,
      chats: s.chats.map((c) =>
        c.id === chatId ? { ...c, messages: [...c.messages, msg], lastActivity: msg.ts } : c
      ),
    })),
  appendDelta: (chatId: string, content: string) =>
    set((s) => ({
      ...s,
      chats: s.chats.map((c) => {
        if (c.id !== chatId) return c;
        const last = c.messages[c.messages.length - 1];
        if (last && last.role === "assistant") {
          const updated = { ...last, content: last.content + content };
          return {
            ...c,
            messages: [...c.messages.slice(0, -1), updated],
            lastActivity: Date.now(),
          };
        }
        const fresh: ChatMessageRecord = {
          id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          role: "assistant",
          content,
          ts: Date.now(),
          toolCalls: [],
          fileEdits: [],
        };
        return { ...c, messages: [...c.messages, fresh], lastActivity: fresh.ts };
      }),
    })),
  markUnread: (chatId: string) =>
    set((s) => {
      if (s.activeId === chatId) return s; // active chat never goes unread
      const unread = new Set(s.unread);
      unread.add(chatId);
      return { ...s, unread };
    }),
  setError: (error: string | null) => set((s) => ({ ...s, error })),
  setLoading: (loading: boolean) => set((s) => ({ ...s, loading })),
  pushApproval: (chatId: string, request: ApprovalRequest) =>
    set((s) => {
      const next = new Map(s.pendingApprovals);
      const existing = next.get(chatId) || [];
      next.set(chatId, [...existing, request]);
      return { ...s, pendingApprovals: next };
    }),
  resolveApproval: (
    chatId: string,
    requestId: string,
    _decision: "approved" | "denied",
    _scope: "once" | "chat"
  ) =>
    set((s) => {
      const next = new Map(s.pendingApprovals);
      const existing = next.get(chatId) || [];
      next.set(
        chatId,
        existing.filter((r) => r.id !== requestId)
      );
      return { ...s, pendingApprovals: next };
    }),
};

export function useChatsStore<T>(selector: (s: ChatsState) => T): T {
  return useSyncExternalStore(
    chatsStore.subscribe,
    () => selector(chatsStore.getSnapshot()),
    () => selector(initial())
  );
}
